/**
 * "The Last Souls" cast gallery — spoiler-free bios for the campaign's
 * recurring cast, shown in the campaign lobby's "Travelers" lineup (CampaignCastGallery).
 *
 * Recast 2026-07-10 in Pokémon Red trainer style (class + short name):
 * docs/superpowers/specs/2026-07-10-campaign-pokered-dialogue-rework.md.
 * Spoiler rules still apply (story bible §3): never reveal Old Man Gray's true
 * identity, the twins' split outcome, Timmy/Chirpy's name-eating, or Aggie's
 * betrayal. Tease, don't tell.
 *
 * Portraits resolve through the shared rival-portrait system
 * (suikamon-web/src/utils/campaignPortraits.ts): a bespoke trainer sprite when
 * one exists for the cast member, otherwise the stage-1 species image fallback.
 * `portraitSpeciesId` below only feeds that fallback.
 */

import { BIREON, SUMIYA, VOLTARYX, PEFAREON, PIKOTO, YETI, TALION } from './builders.js';

/** Which side of the tale a traveler stands on — drives card styling + labels. */
export type CampaignFaction = 'ally' | 'villain';

export interface CampaignCastProfile {
  /** Cast member's name, exactly as it appears as `rivalName` in campaign nodes. */
  name: string;
  /** Short role/title shown under the name. */
  title: string;
  /** Species id used to resolve the portrait image (same mechanism as rival portraits). */
  portraitSpeciesId: number;
  /** One punchy line, <= 12 words. */
  tagline: string;
  /** Spoiler-free flavor text, <= 2 short sentences. */
  bio: string;
  /**
   * Longer, spoiler-safe character notes shown in the click-through detail view
   * (alongside the full-body art). 2–4 short sentences. Optional — falls back to
   * `bio` when absent.
   */
  detail?: string;
  /** Ally traveler (default) or a Clan Z villain. */
  faction?: CampaignFaction;
}

export const CAMPAIGN_CAST_PROFILES: CampaignCastProfile[] = [
  {
    name: 'Timmy',
    title: 'Youngster',
    portraitSpeciesId: BIREON,
    tagline: 'The boy next door. He writes down every win.',
    bio: 'Timmy loves battles and keeps a scorebook to prove it. His chick Chirpy is always ready, and his bunny Mossling eats the corners of the pages.',
    detail:
      'Timmy dreams of being the best Suikamon battler on Suikara. He wants a practice battle before supper and another before breakfast. He gives good advice, too: watch first, then call.',
    faction: 'ally',
  },
  {
    name: 'Mimi',
    title: 'Lass',
    portraitSpeciesId: SUMIYA,
    tagline: 'The first to make a bet. The last to pay.',
    bio: 'Mimi turns everything into a contest, including meeting you. Lulu keeps the score. Mimi keeps suggesting corrections.',
    detail:
      'Mimi will race you to the next tree, challenge your team, and invent a rule halfway through. She is easy to hear coming. She is much harder to distract when her sister needs help.',
    faction: 'ally',
  },
  {
    name: 'Lulu',
    title: 'Lass',
    portraitSpeciesId: SUMIYA,
    tagline: 'Keeps the score. And the evidence.',
    bio: 'Lulu scouts ahead while Mimi makes announcements. Ask her who won their last bet. She has the exact answer, and possibly a receipt.',
    detail:
      'Lulu notices the loose bridge plank, the missing supplies, and the shortcut Mimi swears she found first. She usually lets her sister do the talking. In a battle, she is happy to speak for herself.',
    faction: 'ally',
  },
  {
    name: 'Vera',
    title: 'Channeler',
    portraitSpeciesId: VOLTARYX,
    tagline: 'Leaves supplies. Pretends someone else left them.',
    bio: 'A hooded traveller who hears spirits and asks awkward questions. If she warns you about a path, listen. She has probably walked it.',
    detail:
      'Vera appears at the dangerous turns with a warning and a well-packed bag. She will test your team before letting you past. Ask about her own travels and she changes the subject.',
    faction: 'ally',
  },
  {
    name: 'Cliff',
    title: 'Gatekeeper',
    portraitSpeciesId: PIKOTO,
    tagline: 'Will he open the gate? No. He practices.',
    bio: 'Cliff guards the gate to the Gray Forest, with his grumpy Pikoto at his side. His sister went into the Gray and came home without their mother’s name.',
    detail:
      'Cliff says no fast and means it. He keeps the gate shut because he knows what the Gray takes. But he watches how you battle, and he notices when you stop, think, and ask for help.',
    faction: 'ally',
  },
  {
    name: 'Aggie',
    title: 'Your Gran',
    portraitSpeciesId: PEFAREON,
    tagline: 'Has a story for everything. Almost everything.',
    bio: 'Your gran. She reads you the story of the Heart from her big green book, then hands you the Shard Book with rather more questions than answers.',
    detail:
      'Gran knows the old story of the nineteen purple eggs, and she knows your shell is one of them. Ask her why the Heart broke and she will offer you a biscuit.',
    faction: 'ally',
  },
  {
    name: 'Old Man Gray',
    title: 'The Kind Stranger',
    portraitSpeciesId: YETI,
    tagline: 'Always a place by his fire. Never a name.',
    bio: 'An old traveller who makes room at his campfire. He asks your name, forgets it, then looks terribly sorry when he asks again.',
    detail:
      'You keep meeting him farther down the road. His fire is warm, his pack is nearly empty, and he seems glad of the company. He asks after your journey. By morning, he has walked on.',
    faction: 'ally',
  },
  // ── Clan Z — the recurring villains ─────────────────────────────────────────
  // docs/superpowers/specs/2026-07-12-clan-z-recurring-villains-design.md
  {
    name: 'Zip',
    title: 'Clan Z',
    portraitSpeciesId: TALION,
    tagline: 'Very fast. Especially when the plan goes wrong.',
    bio: 'Zip blocks the trail, announces Clan Z business, and hopes Dash looks frightening enough. His Talion is better at the job than he is.',
    detail:
      'Zip volunteers for the easy jobs, then finds you standing in the middle of them. He is very proud of Dash and less proud of his battle reports. If he shouts about a planned retreat, the plan probably just changed.',
    faction: 'villain',
  },
  {
    name: 'Zap',
    title: 'Clan Z',
    portraitSpeciesId: PEFAREON,
    tagline: "Zip makes the promises. Zap gets the cleanup.",
    bio: 'Zap guards the cages with his Pefareon, Shade. He has usually heard Zip lose before you arrive. He would prefer a different result.',
    detail:
      "Zap watches your team while Zip is still talking. Shade does the same from above. He follows Zeck's orders without much fuss, but lately the jobs are getting harder to explain.",
    faction: 'villain',
  },
  {
    name: 'Zeck the Boss',
    title: 'Clan Z Boss',
    portraitSpeciesId: VOLTARYX,
    tagline: 'Carries the keys. Calls it keeping everyone safe.',
    bio: 'Zeck wants the rarest souls on the island behind his locks. His Voltaryx, Storm, makes arguing with him difficult.',
    detail:
      'Zeck sends Zip and Zap ahead, then arrives when he thinks the prize is worth collecting. He calls his cages protection. Ask who gets to leave and he sends Storm to answer.',
    faction: 'villain',
  },
];
